import React, { useState } from 'react';
import {
  Box, Typography, Stack, Paper, Grid, Chip, CircularProgress, Alert,
  Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Divider, Snackbar, Tooltip, MenuItem,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useQuery, useMutation } from '@apollo/client';
import {
  ROLES_QUERY, PERMISSIONS_QUERY, SUBSCRIPTIONS_QUERY,
  CREATE_ROLE_MUTATION, UPDATE_ROLE_MUTATION, DELETE_ROLE_MUTATION,
  ASSIGN_PERMISSION_TO_ROLE_MUTATION, REMOVE_PERMISSION_FROM_ROLE_MUTATION,
} from '../graphql/queries';

const ROLE_COLORS = {
  system_admin: '#b71c1c', matchmaker: '#6a1b9a',
  bride_family: '#1565c0', groom_family: '#0277bd', vendor: '#e65100',
};

const EMPTY = { name: '', displayName: '', description: '', defaultSubscriptionName: '' };

export default function AdminRolesPage() {
  const { data, loading, error, refetch } = useQuery(ROLES_QUERY);
  const { data: permData } = useQuery(PERMISSIONS_QUERY);
  const { data: subData } = useQuery(SUBSCRIPTIONS_QUERY);
  const [createRole] = useMutation(CREATE_ROLE_MUTATION);
  const [updateRole] = useMutation(UPDATE_ROLE_MUTATION);
  const [deleteRole] = useMutation(DELETE_ROLE_MUTATION);
  const [assignPerm] = useMutation(ASSIGN_PERMISSION_TO_ROLE_MUTATION);
  const [removePerm] = useMutation(REMOVE_PERMISSION_FROM_ROLE_MUTATION);

  const [dialog, setDialog]         = useState({ open: false, mode: 'create', item: EMPTY });
  const [permDialog, setPermDialog] = useState({ open: false, roleId: null, roleName: '', rolePerms: [] });
  const [confirmDel, setConfirmDel] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [snack, setSnack]           = useState({ open: false, msg: '', severity: 'success' });

  const roles    = data?.roles        || [];
  const allPerms = permData?.permissions || [];
  const subscriptions = subData?.subscriptions || [];
  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const handleSave = async () => {
    const { mode, item } = dialog;
    setSaving(true);
    try {
      if (mode === 'create') {
        await createRole({ variables: { input: { name: item.name, displayName: item.displayName, description: item.description, defaultSubscriptionName: item.defaultSubscriptionName || null } } });
        showSnack('Role created');
      } else {
        await updateRole({ variables: { input: { id: item.id, displayName: item.displayName, description: item.description, defaultSubscriptionName: item.defaultSubscriptionName || null } } });
        showSnack('Role updated');
      }
      await refetch();
      setDialog({ open: false, mode: 'create', item: EMPTY });
    } catch (e) { showSnack(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    setSaving(true);
    try {
      await deleteRole({ variables: { id } });
      await refetch();
      setConfirmDel(null);
      showSnack('Role deleted');
    } catch (e) { showSnack(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleTogglePerm = async (permId, has) => {
    const { roleId } = permDialog;
    try {
      if (has) await removePerm({ variables: { roleId, permissionId: permId } });
      else     await assignPerm({ variables: { roleId, permissionId: permId } });
      const updated = await refetch();
      const updatedRole = updated.data.roles.find(r => r.id === roleId);
      if (updatedRole) setPermDialog(d => ({ ...d, rolePerms: updatedRole.permissions }));
    } catch (e) { showSnack(e.message, 'error'); }
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error" sx={{ m: 3 }}>{error.message}</Alert>;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <SecurityIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Roles</Typography>
            <Typography variant="body2" color="text.secondary">Create and manage platform roles and their permissions</Typography>
          </Box>
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => setDialog({ open: true, mode: 'create', item: EMPTY })}>
          New Role
        </Button>
      </Stack>

      <Grid container spacing={3}>
        {roles.map(role => (
          <Grid item xs={12} md={6} key={role.id}>
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ bgcolor: ROLE_COLORS[role.name] || '#37474f', p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="white">{role.displayName}</Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>{role.name}</Typography>
                    {role.description && (
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>{role.description}</Typography>
                    )}
                  </Box>
                  <Stack direction="row">
                    <Tooltip title="Edit"><IconButton size="small" sx={{ color: 'white' }}
                      onClick={() => setDialog({ open: true, mode: 'edit', item: { id: role.id, name: role.name, displayName: role.displayName, description: role.description || '', defaultSubscriptionName: role.defaultSubscription?.name || '' } })}>
                      <EditIcon fontSize="small" />
                    </IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" sx={{ color: 'rgba(255,255,255,0.7)' }}
                      onClick={() => setConfirmDel(role.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton></Tooltip>
                  </Stack>
                </Stack>
              </Box>
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" mb={1.5}>
                  New users get the{' '}
                  <Chip size="small" color="primary" label={role.defaultSubscription?.displayName || 'Free'} />{' '}
                  subscription with this role
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                    {role.permissions.length} PERMISSIONS
                  </Typography>
                  <Button size="small" variant="outlined"
                    onClick={() => setPermDialog({ open: true, roleId: role.id, roleName: role.displayName, rolePerms: role.permissions })}>
                    Manage
                  </Button>
                </Stack>
                <Divider sx={{ mb: 1.5 }} />
                <Stack direction="row" flexWrap="wrap" spacing={0.5} useFlexGap>
                  {role.permissions.map(p => (
                    <Chip key={p.id} label={p.displayName} size="small" icon={<CheckCircleIcon />} color="success" variant="outlined" />
                  ))}
                  {role.permissions.length === 0 && <Typography variant="caption" color="text.secondary">No permissions assigned</Typography>}
                </Stack>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Create / Edit dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog.mode === 'create' ? 'Create Role' : 'Edit Role'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {dialog.mode === 'create' && (
              <TextField label="Name (code key)" value={dialog.item.name} size="small" fullWidth required
                placeholder="e.g. vendor_premium"
                onChange={e => setDialog(d => ({ ...d, item: { ...d.item, name: e.target.value.toLowerCase().replace(/ /g, '_') } }))} />
            )}
            <TextField label="Display Name" value={dialog.item.displayName} size="small" fullWidth required
              onChange={e => setDialog(d => ({ ...d, item: { ...d.item, displayName: e.target.value } }))} />
            <TextField label="Description" value={dialog.item.description} size="small" fullWidth multiline rows={2}
              onChange={e => setDialog(d => ({ ...d, item: { ...d.item, description: e.target.value } }))} />
            <TextField select label="Default subscription for new users" value={dialog.item.defaultSubscriptionName} size="small" fullWidth
              helperText="Users created with this role get this subscription; their permissions are the role's plus the subscription's"
              onChange={e => setDialog(d => ({ ...d, item: { ...d.item, defaultSubscriptionName: e.target.value } }))}>
              <MenuItem value=""><em>Free (none set)</em></MenuItem>
              {subscriptions.map(s => <MenuItem key={s.id} value={s.name}>{s.displayName}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(d => ({ ...d, open: false }))}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permissions assignment dialog */}
      <Dialog open={permDialog.open} onClose={() => setPermDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>Permissions — {permDialog.roleName}</DialogTitle>
        <DialogContent>
          <Stack spacing={1} mt={1}>
            {allPerms.map(p => {
              const has = permDialog.rolePerms?.some(rp => rp.id === p.id);
              return (
                <Stack key={p.id} direction="row" justifyContent="space-between" alignItems="center"
                  sx={{ p: 1, borderRadius: 1, bgcolor: has ? '#e8f5e9' : 'grey.50',
                    border: '1px solid', borderColor: has ? '#a5d6a7' : 'grey.200' }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{p.displayName}</Typography>
                    <Typography variant="caption" color="text.secondary">{p.description}</Typography>
                  </Box>
                  <Button size="small" color={has ? 'error' : 'success'} variant="outlined"
                    onClick={() => handleTogglePerm(p.id, has)}>
                    {has ? 'Remove' : 'Add'}
                  </Button>
                </Stack>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermDialog(d => ({ ...d, open: false }))}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDel} onClose={() => setConfirmDel(null)}>
        <DialogTitle>Delete Role?</DialogTitle>
        <DialogContent><Typography>Users with this role will lose their role assignment.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(confirmDel)} disabled={saving}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
